import { BrandAuthProvider, buildAuthConfig, type BrandAuthConfig } from "@nextday/brand-auth";
import { useMemo, type ReactNode } from "react";
import {
  useApplicationContext,
  type ApplicationContextState,
} from "@src/funnel/useApplicationContext";

/**
 * Mounts {@link BrandAuthProvider} once the runtime application context has
 * resolved and the application has per-app auth config (kindeDomain,
 * kindeOrgCode, appOrigin) from D1. Until then, or for applications without
 * auth config, children render directly so the rest of the funnel still
 * works.
 */
export type FunnelAuthShellProps = {
  children: ReactNode;
  /** Override for tests. Defaults to live `useApplicationContext`. */
  contextOverride?: ApplicationContextState;
  /**
   * When set, skip application-context resolution and mount the provider
   * directly with this config. Used during the auth callback render: the
   * URL at that point is the funnel root (no campaign slug), so we can't
   * resolve the app from the application context — the config was
   * persisted to sessionStorage at register/login time and is restored
   * by `App.tsx` before this component mounts.
   */
  configOverride?: BrandAuthConfig;
};

const FunnelAuthShellLoader = () => (
  <div className="flex h-dvh w-full items-center justify-center bg-[hsl(var(--brand-bg))] text-[hsl(var(--brand-bg-foreground))]">
    <div className="size-10 animate-spin rounded-full border-2 border-[hsl(var(--brand-bg-foreground)/0.2)] border-t-[hsl(var(--brand-bg-foreground))]" />
  </div>
);

export const FunnelAuthShell = (props: FunnelAuthShellProps) => {
  const { children, contextOverride, configOverride } = props;
  const live = useApplicationContext();
  const context = contextOverride ?? live;

  const redirectUri = useMemo(() => window.location.origin, []);

  if (configOverride) {
    return (
      <BrandAuthProvider config={configOverride} redirectUri={redirectUri}>
        {children}
      </BrandAuthProvider>
    );
  }

  if (context.status === "loading") {
    return <FunnelAuthShellLoader />;
  }

  if (context.status !== "ready") {
    return <>{children}</>;
  }

  const { application } = context;
  if (!application.kindeDomain || !application.kindeOrgCode || !application.appOrigin) {
    return <>{children}</>;
  }

  const config = buildAuthConfig({
    kindeDomain: application.kindeDomain,
    kindeOrgCode: application.kindeOrgCode,
    appOrigin: application.appOrigin,
    kindeClientId: application.kindeClientId ?? undefined,
  });

  return (
    <BrandAuthProvider config={config} redirectUri={redirectUri}>
      {children}
    </BrandAuthProvider>
  );
};
